import type { Language } from '@/config/objects/languages';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  Input,
  Skeleton,
  Switch,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

interface LanguageFormData {
  name: string;
  code: string;
  isDefault: boolean;
}

const DEFAULT_FORM: LanguageFormData = {
  name: '',
  code: '',
  isDefault: false,
};

export function LanguageFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<LanguageFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  const updateField = useCallback(
    <K extends keyof LanguageFormData>(key: K, value: LanguageFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // Load existing language (edit mode)
  const {
    data: languageResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: Language }>({
    queryKey: ['Language', itemId],
    queryFn: () => apiClient.get(`/languages/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!languageResponse?.data) return;
    const a = languageResponse.data;
    setFormData({
      name: a.name ?? '',
      code: a.code ?? '',
      isDefault: a.isDefault ?? false,
    });
    setLastSaved(new Date(a.updatedAt));
    setFormReady(true);
  }, [languageResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Language not found');
      navigate('/languages');
    }
  }, [isError, navigate]);

  // Validation
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.code.trim()) {
      errs.code = 'Code is required';
    } else if (!/^[a-z]{2,3}$/.test(formData.code.trim().toLowerCase())) {
      errs.code = 'Code must be 2-3 lowercase letters (e.g., en, spa)';
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`field-${firstKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  // Prepare payload
  const preparePayload = () => ({
    name: formData.name.trim(),
    code: formData.code.trim().toLowerCase(),
    isDefault: formData.isDefault,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: Language }>(`/languages/${itemId}`, payload);
      }
      return apiClient.post<{ data: Language }>('/languages', payload);
    },
    onSuccess: (response: { data: Language }) => {
      queryClient.invalidateQueries({ queryKey: ['Language'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Language saved successfully');
        navigate(-1);
      } else {
        toast.success('Language created successfully');
        navigate(`/languages/${response.data.id}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save language');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;
    saveMutation.mutate(preparePayload());
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/languages/${itemId}`);
    } else {
      navigate('/languages');
    }
  };

  // Loading skeleton
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Language' : 'Create Language'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? formData.name || 'Update language details'
                : 'Add a new language to the platform'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div ref={firstErrorRef} className="space-y-6">
          {/* General Information */}
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 border-b">
              <CardTitle className="text-zinc-900">General Information</CardTitle>
              <CardDescription>Core language details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter language name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="code"
                label="Code"
                required
                error={errors.code}
                description="ISO 639-1 or 639-2 language code (e.g., en, es, fra)"
              >
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => updateField('code', e.target.value)}
                  placeholder="en"
                  className={cn(errors.code && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="isDefault"
                label="Default Language"
                description="Set this as the default language for the platform"
              >
                <Switch
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => updateField('isDefault', checked)}
                />
              </FormFieldGroup>
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
